import {resolve} from "../../../container";
import {HorizontalOverflow, HorizontalOverflowInstaller} from "../horizontalOverflow";
import {prepareEvents} from "../../../common/utils/events";
import {html, type PropertyValues, type TemplateResult} from "lit";
import {customElement, property, query} from "lit/decorators.js";
import {unsafeSVG} from "lit/directives/unsafe-svg.js";
import {fresh, ManagingResources} from "../../../common/lifetime";
import {PopoverAnchor} from "../popover/popoverAnchor";
import {trackingButtonInteract} from "../../../tracking/trackingAttributes";
import {DialogAnchorTemplateContent} from "../dialog/dialogAnchorTemplateContent";
import {UnLitElement} from "../../../common/elements";
import type {Nullable} from "../../../bootstrap/common/types";
import {PLUS_ICON} from "../../../common/svgIcons";

export class EopHotspots extends HTMLElement {
    private horizontalOverflow: HorizontalOverflow;

    public constructor(private horizontalOverflowInstaller: HorizontalOverflowInstaller = resolve(HorizontalOverflowInstaller)) {
        super();
    }

    public connectedCallback(): void {
        const scrollContentSelector = this.getAttribute("scroll-content-selector");
        const contentElement = (scrollContentSelector ? this.querySelector(scrollContentSelector) : this) ?? this;
        this.horizontalOverflow = this.horizontalOverflowInstaller.installOn(this, contentElement);
    }

    public disconnectedCallback(): void {
        this.horizontalOverflow.uninstall();
    }
}

export enum HotspotIndicatorType {
    ENUMERATE = "hotspot-indicator-enumerate",
    PLUS = "hotspot-indicator-plus"
}

@customElement("eop-hotspot-with-page-overlay")
export class EopHotspotWithPageOverlay extends UnLitElement {
    @property({attribute: "label"})
    public label: string;
    @property({attribute: "label-pos"})
    public labelPos: string;
    @property({attribute: "href"})
    public href: string;
    @property({attribute: "indicator-type"})
    public indicatorType: HotspotIndicatorType;

    public render(): TemplateResult {
        return html`
            <eop-page-overlay-link 
                href=${this.href} 
                title=${this.label}
                ${trackingButtonInteract(this.label)}
                class="hotspot-interaction-item"
            >
                <eop-hotspot label=${this.label} label-pos=${this.labelPos} indicator-type=${this.indicatorType}></eop-hotspot>
            </eop-page-overlay-link>
        `;
    }
}

@customElement("eop-hotspot-with-layer")
export class EopHotspotWithLayer extends DialogAnchorTemplateContent {

    @property({attribute: "label"})
    public label: string;
    @property({attribute: "label-pos"})
    public labelPos: string;
    @property({attribute: "indicator-type"})
    public indicatorType: HotspotIndicatorType;

    public render(): TemplateResult {
        return html`
            <button
                    @click=${this.openLayer}
                    title=${this.label}
                    aria-label=${(this.label)}
                    ${trackingButtonInteract(this.label)}
                    class="hotspot-interaction-item"
            >
                <eop-hotspot label=${this.label} label-pos=${this.labelPos} indicator-type=${this.indicatorType}></eop-hotspot>
            </button>
        `;
    }

    private openLayer(e: Event): void {
        this.openDialog(e);
    }
}

@customElement("eop-hotspot-with-popover")
export class EopHotspotWithPopover extends PopoverAnchor {

    @query("button")
    public button: HTMLButtonElement;
    @property({attribute: "label"})
    public label: string;
    @property({attribute: "label-pos"})
    public labelPos: string;
    @property({attribute: "indicator-type"})
    public indicatorType: HotspotIndicatorType;

    public render(): TemplateResult {
        return html`
            <button 
                title=${this.label} 
                aria-label=${(this.label)}
                ${trackingButtonInteract(this.label)}
                class="hotspot-interaction-item"
            >
                <eop-hotspot label=${this.label} label-pos=${this.labelPos} indicator-type=${this.indicatorType}></eop-hotspot>
            </button>
        `;
    }

    public getAnchorElement(): HTMLElement {
        return this.button;
    }

    protected updated(_changedProperties: PropertyValues): void {
        super.updated(_changedProperties);

        const lifetime = fresh(this, "firstUpdated");
        this.popoverElement.enforceCloseButton = true;
        prepareEvents(this)
            .boundTo(lifetime)
            .on("click", () => {
                this.setPopoverPosition();
                this.popoverElement.open();
            });
    }
}

@customElement("eop-hotspot")
export class EopHotspot extends ManagingResources(UnLitElement) {

    @property({attribute: "label"})
    public label: string;
    @property({attribute: "label-pos"})
    public labelPosition: string;
    @property({attribute: "indicator-type"})
    public indicatorType: HotspotIndicatorType;

    public render(): TemplateResult {
        return html`
            <span class="${this.labelPosition} hotspot-label" tabindex="-1">
                ${this.label}
            </span>
            <span class="hotspot-indicator">
                ${this.renderIndicator()}
            </span>
        `;
    }

    private renderIndicator(): Nullable<TemplateResult> {
        if (this.indicatorType === HotspotIndicatorType.ENUMERATE) {
            return html`<span class="indicator-enumerate"></span>`;
        }
        if (this.indicatorType === HotspotIndicatorType.PLUS) {
            return html`
            <span class="indicator-plus">
                    ${unsafeSVG(PLUS_ICON)}
            </span>`;

        }
        return null;
    }
}

customElements.define("eop-hotspots", EopHotspots);